import React, { useState, useEffect } from 'react';
import { User } from '@supabase/supabase-js';
import { FileText, Plus } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useBetaAssignments, useAssignmentFeedback } from '../../../hooks/useBetaAssignments';
import AssignmentList from './AssignmentList';
import AssignmentView from './AssignmentView';
import AssignmentForm from './AssignmentForm';
import type { BetaAssignment, Chapter } from '../../../types';

interface AssignmentManagerProps {
  user: User;
}

export default function AssignmentManager({ user }: AssignmentManagerProps) {
  const [selectedBookId, setSelectedBookId] = useState<string | null>(null);
  const [selectedAssignment, setSelectedAssignment] = useState<BetaAssignment | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [applications, setApplications] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { assignments, createAssignment, updateAssignmentStatus, refresh } = 
    useBetaAssignments(selectedBookId || '');
  const { feedback, addFeedback } = useAssignmentFeedback(selectedAssignment?.id || '');

  useEffect(() => {
    loadBooks();
  }, [user.id]);

  useEffect(() => {
    if (selectedBookId) {
      loadChapters();
      loadApplications();
    }
  }, [selectedBookId]);

  const loadBooks = async () => {
    try {
      const { data: books, error: booksError } = await supabase
        .from('book_ideas')
        .select('id, title')
        .eq('author_id', user.id)
        .eq('accepting_beta_readers', true)
        .order('created_at', { ascending: false });

      if (booksError) throw booksError;

      if (books?.length > 0) {
        setSelectedBookId(books[0].id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load books');
    } finally {
      setLoading(false);
    }
  };

  const loadChapters = async () => {
    if (!selectedBookId) return;

    try {
      const { data: chaptersData, error: chaptersError } = await supabase
        .from('chapters')
        .select('*')
        .eq('book_id', selectedBookId)
        .order('position', { ascending: true });

      if (chaptersError) throw chaptersError;
      setChapters(chaptersData || []);
    } catch (err) {
      console.error('Error loading chapters:', err);
    }
  };

  const loadApplications = async () => {
    if (!selectedBookId) return;

    try {
      const { data: applicationsData, error: applicationsError } = await supabase
        .from('beta_reader_applications')
        .select(`
          id,
          reader_id,
          status,
          profiles:reader_id(
            username,
            full_name
          )
        `)
        .eq('idea_id', selectedBookId)
        .eq('status', 'approved');

      if (applicationsError) throw applicationsError;
      setApplications(applicationsData || []);
    } catch (err) {
      console.error('Error loading applications:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500" />
      </div>
    );
  }

  if (!selectedBookId) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="text-center py-8">
          <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Books Available</h3>
          <p className="text-gray-600">
            You need to have a book accepting beta readers before you can create assignments.
          </p>
        </div>
      </div>
    );
  }

  if (applications.length === 0 && !selectedAssignment) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="text-center py-8">
          <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Approved Beta Readers</h3>
          <p className="text-gray-600">
            You need to approve some beta reader applications before you can create assignments.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <FileText className="w-5 h-5 text-violet-500" />
          Beta Reader Assignments
        </h2>
        {!selectedAssignment && !showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700"
          >
            <Plus className="w-4 h-4" />
            Create Assignment
          </button>
        )}
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {showForm ? (
        <AssignmentForm
          ideaId={selectedBookId}
          applications={applications}
          chapters={chapters}
          onSubmit={createAssignment}
          onCancel={() => setShowForm(false)}
        />
      ) : selectedAssignment ? (
        <AssignmentView
          assignment={selectedAssignment}
          feedback={feedback}
          isAuthor={true}
          onBack={() => setSelectedAssignment(null)}
          onUpdateStatus={async (status) => {
            await updateAssignmentStatus(selectedAssignment.id, status);
            await refresh();
          }}
          onSubmitFeedback={addFeedback}
        />
      ) : (
        <AssignmentList
          assignments={assignments}
          isAuthor={true}
          onSelect={setSelectedAssignment}
        />
      )}
    </div>
  );
}